import { serializeRouteData, deserializeRouteData } from "astro/app/manifest";
import { StaticPaths } from "astro:static-paths";
import {
  STATIC_PATHS_ENDPOINT,
  PRERENDER_ENDPOINT,
  STATIC_IMAGES_ENDPOINT
} from "./prerender-constants.js";
function installPrerenderErrorPropagation(app) {
  const originalRenderError = app.renderError.bind(app);
  app.renderError = async (request, options) => {
    if (options.status === 500) {
      if (options.response) {
        return options.response;
      }
      throw options.error;
    }
    return originalRenderError(request, options);
  };
}
function isStaticPathsRequest(request) {
  const { pathname } = new URL(request.url);
  return pathname === STATIC_PATHS_ENDPOINT && request.method === "POST";
}
function isPrerenderRequest(request) {
  const { pathname } = new URL(request.url);
  return pathname === PRERENDER_ENDPOINT && request.method === "POST";
}
async function handleStaticPathsRequest(app) {
  const staticPaths = new StaticPaths(app);
  const paths = await staticPaths.getAll();
  const response = {
    paths: paths.map(({ pathname, route }) => ({
      pathname,
      route: serializeRouteData(route, app.manifest.trailingSlash)
    }))
  };
  return new Response(JSON.stringify(response), {
    headers: { "Content-Type": "application/json" }
  });
}
async function handlePrerenderRequest(app, request) {
  const headers = new Headers();
  for (const [key, value] of request.headers.entries()) {
    headers.append(key, value);
  }
  const body = await request.json();
  const routeData = deserializeRouteData(body.routeData);
  const prerenderRequest = new Request(body.url, {
    method: "GET",
    headers
  });
  try {
    const response = await app.render(prerenderRequest, { routeData });
    const bufferedBody = await response.arrayBuffer();
    return new Response(bufferedBody, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return new Response(message, {
      status: 500,
      headers: {
        "Content-Type": "text/plain",
        "x-astro-prerender-error": message
      }
    });
  }
}
function isStaticImagesRequest(request) {
  const { pathname } = new URL(request.url);
  return pathname === STATIC_IMAGES_ENDPOINT && request.method === "POST";
}
function handleStaticImagesRequest() {
  const staticImages = globalThis.astroAsset?.staticImages;
  if (!staticImages || staticImages.size === 0) {
    return new Response("[]", {
      headers: { "Content-Type": "application/json" }
    });
  }
  const entries = [];
  for (const [originalPath, { originalSrcPath, transforms }] of staticImages) {
    const serializedTransforms = [];
    for (const [hash, { finalPath, transform }] of transforms) {
      serializedTransforms.push({
        hash,
        finalPath,
        transform
      });
    }
    entries.push({ originalPath, originalSrcPath, transforms: serializedTransforms });
  }
  return new Response(JSON.stringify(entries), {
    headers: { "Content-Type": "application/json" }
  });
}
export {
  handlePrerenderRequest,
  handleStaticImagesRequest,
  handleStaticPathsRequest,
  installPrerenderErrorPropagation,
  isPrerenderRequest,
  isStaticImagesRequest,
  isStaticPathsRequest
};
